import { useState } from 'react'
import { Phone, Mail, MapPin, Clock, Send, Check, MessageCircle } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useRecaptcha } from '../hooks/useRecaptcha'
import { API_CONFIG } from '../config/api'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'
import { BRAND } from '../theme/brand'

// 2Life Travel hardcoded contact details (overrides API fallback)
const CONTACT = {
  mobile: '+27731754254',
  office1: '0123615924',
  office2: '0218795729',
  email: 'info@2lifetravel.co.za',
  address: 'Lynnwood Ridge, Pretoria',
  facebook: 'https://www.facebook.com/2LifeTravel',
  whatsapp: '+27832910431',
  advisors: [
    { name: 'Adriaan Kruger', title: 'Director and owner' },
    { name: 'Sonia Kruger', title: 'Senior Travel Advisor' },
  ],
}

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pax: '',
    dates: '',
    origin: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const buildFriendlyApiError = (status: number, bodyText: string) => {
    if (status === 401 || status === 403) {
      return 'Submission was rejected by the API (authorization failed). Please verify your API key and allowed domains.'
    }
    if (status === 422) {
      return bodyText || 'Submission was rejected because some fields are invalid. Please review your details and try again.'
    }
    if (status >= 500) {
      return 'The server is currently unavailable. Please try again in a few minutes.'
    }
    return bodyText || `Submission failed with status ${status}.`
  }

  const { franchiseData, loading } = useFranchiseData()
  const { token: recaptchaToken, widgetRef: recaptchaWidgetRef, reset: resetRecaptcha, enabled: recaptchaEnabled } = useRecaptcha()

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    if (recaptchaEnabled && !recaptchaToken) {
      setSubmitError(
        'Please complete the reCAPTCHA checkbox before submitting. If the checkbox does not appear, confirm this domain is allowed for your site key.'
      )
      setIsSubmitting(false)
      return
    }

    try {
      const params = new URLSearchParams({
        'lead[first_name]': formData.firstName,
        'lead[last_name]': formData.lastName,
        'lead[email]': formData.email,
        'lead[telephone]': formData.phone,
        'lead[message]': formData.message.trim() || 'no message',
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': formData.pax,
        'lead[dates]': formData.dates,
        'lead[origin]': formData.origin,
        ...(recaptchaToken ? { 'g-recaptcha-response': recaptchaToken } : {}),
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        setSubmitSuccess(true)
        resetRecaptcha()
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          pax: '',
          dates: '',
          origin: '',
          message: '',
        })
        setTimeout(() => {
          setSubmitSuccess(false)
        }, 5000)
      } else {
        const errorData = (await response.text()).trim()
        const friendlyError = buildFriendlyApiError(response.status, errorData)
        console.error('API Error:', response.status, errorData)
        throw new Error(friendlyError)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send your message. Please try again.'
      setSubmitError(message)
      console.error('Contact form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading || !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src="/2life-logo.png" alt="2Life Travel" className="h-20 w-auto mx-auto mb-6 opacity-50" />
          <div className="h-1 w-32 mx-auto rounded-full overflow-hidden bg-gray-200">
            <div className="h-full rounded-full animate-pulse" style={{ backgroundColor: BRAND.maroon, width: '60%' }}></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      <Helmet>
        <title>Contact Us | 2Life Travel</title>
        <meta
          name="description"
          content="Get in touch with 2Life Travel's expert consultants for tailored holiday planning and support. Based in Pretoria, South Africa."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/contact' : '/contact'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Us | 2Life Travel" />
        <meta
          property="og:description"
          content="Speak to our travel experts and plan your perfect trip."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/contact'}
        />
        <meta property="og:image" content="/2life-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Us',
            url: typeof window !== 'undefined' ? window.location.href : '/contact',
            description:
              "Get in touch with 2Life Travel's expert consultants for tailored holiday planning and support.",
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Contact',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/contact',
              },
            ],
          },
        ]}
      />

      {/* ==================== HERO BANNER ==================== */}
      <div
        className="relative py-20 sm:py-28 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.maroon} 100%)`,
        }}
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }} />
        </div>
        <div className="absolute top-0 left-0 w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}50, transparent)` }} />

        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <svg width="28" height="28" viewBox="0 0 40 40" className="mx-auto mb-6" style={{ opacity: 0.6 }}>
            <path d="M20 0 L22 16 L40 20 L22 22 L20 40 L18 22 L0 20 L18 16 Z" fill={BRAND.gold} />
          </svg>
          <p
            className="text-sm tracking-[0.3em] uppercase mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.gold, fontWeight: 500 }}
          >
            Get in Touch
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Let's Plan Your{' '}
            <span className="italic font-medium" style={{ color: BRAND.gold }}>Dream</span> Journey
          </h1>
          <p
            className="mt-6 text-base text-white/70 max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
          >
            Ready to explore the world? Our expert consultants are here to craft
            the perfect itinerary tailored just for you.
          </p>
        </div>

        <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}50, transparent)` }} />
      </div>

      {/* ==================== MAIN CONTENT ==================== */}
      <div className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-5">

            {/* ===== LEFT: Contact Info (2 cols) ===== */}
            <div className="lg:col-span-2">
              <p
                className="text-sm tracking-[0.2em] uppercase mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
              >
                Contact Details
              </p>
              <h2
                className="text-3xl font-light mb-10"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
              >
                Reach <span className="italic font-medium" style={{ color: BRAND.maroon }}>Out</span>
              </h2>

              <div className="space-y-8">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-11 w-11 items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${BRAND.maroon}10`, borderRadius: '2px', border: `1px solid ${BRAND.maroon}20` }}
                  >
                    <Phone className="h-5 w-5" style={{ color: BRAND.maroon }} />
                  </div>
                  <div>
                    <h4
                      className="text-sm font-medium mb-1"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                    >
                      Phone Numbers
                    </h4>
                    <div className="space-y-1">
                      <a
                        href={`tel:${CONTACT.mobile.replace(/\s/g, '')}`}
                        className="block text-base hover:opacity-70 transition-opacity"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon, fontWeight: 500 }}
                      >
                        Mobile: {CONTACT.mobile}
                      </a>
                      <a
                        href={`tel:${CONTACT.office1.replace(/\s/g, '')}`}
                        className="block text-base hover:opacity-70 transition-opacity"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon, fontWeight: 500 }}
                      >
                        Office: {CONTACT.office1}
                      </a>
                      <a
                        href={`tel:${CONTACT.office2.replace(/\s/g, '')}`}
                        className="block text-base hover:opacity-70 transition-opacity"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon, fontWeight: 500 }}
                      >
                        {CONTACT.office2}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-11 w-11 items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${BRAND.maroon}10`, borderRadius: '2px', border: `1px solid ${BRAND.maroon}20` }}
                  >
                    <Mail className="h-5 w-5" style={{ color: BRAND.maroon }} />
                  </div>
                  <div>
                    <h4
                      className="text-sm font-medium mb-1"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                    >
                      Email
                    </h4>
                    <a
                      href={`mailto:${CONTACT.email}`}
                      className="text-base hover:opacity-70 transition-opacity"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon, fontWeight: 500 }}
                    >
                      {CONTACT.email}
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-11 w-11 items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${BRAND.maroon}10`, borderRadius: '2px', border: `1px solid ${BRAND.maroon}20` }}
                  >
                    <MapPin className="h-5 w-5" style={{ color: BRAND.maroon }} />
                  </div>
                  <div>
                    <h4
                      className="text-sm font-medium mb-1"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                    >
                      Address
                    </h4>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 400 }}
                    >
                      {CONTACT.address}
                    </p>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-11 w-11 items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${BRAND.maroon}10`, borderRadius: '2px', border: `1px solid ${BRAND.maroon}20` }}
                  >
                    <Clock className="h-5 w-5" style={{ color: BRAND.maroon }} />
                  </div>
                  <div>
                    <h4
                      className="text-sm font-medium mb-1"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                    >
                      Working Hours
                    </h4>
                    <div
                      className="text-sm leading-relaxed space-y-0.5"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 400 }}
                    >
                      <p>Monday – Friday: 9:00 AM – 5:00 PM</p>
                      <p>Saturday: 9:00 AM – 1:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-11 w-11 items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${BRAND.maroon}10`, borderRadius: '2px', border: `1px solid ${BRAND.maroon}20` }}
                  >
                    <MessageCircle className="h-5 w-5" style={{ color: BRAND.maroon }} />
                  </div>
                  <div>
                    <h4
                      className="text-sm font-medium mb-1"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                    >
                      WhatsApp
                    </h4>
                    <a
                      href={`https://api.whatsapp.com/send?phone=${CONTACT.whatsapp.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:opacity-70 transition-opacity"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon, fontWeight: 500 }}
                    >
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div
                className="mt-10 pt-8"
                style={{ borderTop: `1px solid ${BRAND.cream}` }}
              >
                <p
                  className="text-xs tracking-[0.2em] uppercase mb-4"
                  style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
                >
                  Follow Us
                </p>
                <div className="flex gap-4">
                  <a
                    href={CONTACT.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm transition-all duration-300 hover:opacity-80"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      color: BRAND.maroon,
                      backgroundColor: `${BRAND.maroon}08`,
                      border: `1px solid ${BRAND.maroon}15`,
                      borderRadius: '2px',
                      fontWeight: 500,
                    }}
                  >
                    Facebook
                  </a>
                  <a
                    href={`https://api.whatsapp.com/send?phone=${CONTACT.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm transition-all duration-300 hover:opacity-80"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      color: BRAND.maroon,
                      backgroundColor: `${BRAND.maroon}08`,
                      border: `1px solid ${BRAND.maroon}15`,
                      borderRadius: '2px',
                      fontWeight: 500,
                    }}
                  >
                    WhatsApp
                  </a>
                </div>
              </div>

              {/* Consultant card */}
              <div
                className="mt-10 p-6"
                style={{
                  backgroundColor: BRAND.cream,
                  borderRadius: '2px',
                  borderLeft: `3px solid ${BRAND.gold}`,
                }}
              >
                <p
                  className="text-xs tracking-[0.2em] uppercase mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
                >
                  Your Travel Advisors
                </p>
                <div className="space-y-4">
                  {CONTACT.advisors.map((advisor) => (
                    <div key={advisor.name}>
                      <p
                        className="text-lg font-medium"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
                      >
                        {advisor.name}
                      </p>
                      <p
                        className="text-sm mt-1"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
                      >
                        {advisor.title}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ===== RIGHT: Contact Form (3 cols) ===== */}
            <div className="lg:col-span-3">
              <div
                className="p-8 sm:p-10"
                style={{
                  backgroundColor: BRAND.cream,
                  borderRadius: '2px',
                  borderTop: `3px solid ${BRAND.maroon}`,
                }}
              >
                <p
                  className="text-sm tracking-[0.2em] uppercase mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
                >
                  Send a Message
                </p>
                <h2
                  className="text-3xl font-light mb-8"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
                >
                  Plan Your <span className="italic font-medium" style={{ color: BRAND.maroon }}>Trip</span>
                </h2>

                {submitSuccess ? (
                  <div className="text-center py-12">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: `${BRAND.maroon}15` }}
                    >
                      <Check className="h-8 w-8" style={{ color: BRAND.maroon }} />
                    </div>
                    <h4
                      className="text-xl font-medium mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
                    >
                      Message Sent Successfully!
                    </h4>
                    <p
                      className="text-sm"
                      style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
                    >
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="firstName"
                          className="block text-xs font-medium uppercase tracking-wider mb-2"
                          style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                        >
                          First Name <span style={{ color: BRAND.maroon }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          id="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '14px',
                            borderColor: BRAND.taupeLight,
                            borderRadius: '2px',
                            outline: 'none',
                          }}
                          onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                          onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                          placeholder="Enter your first name"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="lastName"
                          className="block text-xs font-medium uppercase tracking-wider mb-2"
                          style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                        >
                          Last Name <span style={{ color: BRAND.maroon }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          id="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '14px',
                            borderColor: BRAND.taupeLight,
                            borderRadius: '2px',
                            outline: 'none',
                          }}
                          onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                          onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-medium uppercase tracking-wider mb-2"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                      >
                        Email Address <span style={{ color: BRAND.maroon }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '14px',
                          borderColor: BRAND.taupeLight,
                          borderRadius: '2px',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                        onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-xs font-medium uppercase tracking-wider mb-2"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                      >
                        Phone Number <span style={{ color: BRAND.maroon }}>*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '14px',
                          borderColor: BRAND.taupeLight,
                          borderRadius: '2px',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                        onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                        placeholder="+27 XX XXX XXXX"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="pax"
                          className="block text-xs font-medium uppercase tracking-wider mb-2"
                          style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                        >
                          Number of People <span style={{ color: BRAND.maroon }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="pax"
                          id="pax"
                          required
                          value={formData.pax}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '14px',
                            borderColor: BRAND.taupeLight,
                            borderRadius: '2px',
                            outline: 'none',
                          }}
                          onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                          onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                          placeholder="e.g., 2 adults, 1 child"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="dates"
                          className="block text-xs font-medium uppercase tracking-wider mb-2"
                          style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                        >
                          Travel Dates <span style={{ color: BRAND.maroon }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="dates"
                          id="dates"
                          required
                          value={formData.dates}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '14px',
                            borderColor: BRAND.taupeLight,
                            borderRadius: '2px',
                            outline: 'none',
                          }}
                          onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                          onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                          placeholder="e.g., March 2026"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="origin"
                        className="block text-xs font-medium uppercase tracking-wider mb-2"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                      >
                        Departure City <span style={{ color: BRAND.maroon }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="origin"
                        id="origin"
                        required
                        value={formData.origin}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 bg-white border transition-all duration-200"
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '14px',
                          borderColor: BRAND.taupeLight,
                          borderRadius: '2px',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                        onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                        placeholder="e.g., Johannesburg, Pretoria"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-xs font-medium uppercase tracking-wider mb-2"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                      >
                        Additional Message
                      </label>
                      <textarea
                        name="message"
                        id="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 bg-white border transition-all duration-200 resize-none"
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '14px',
                          borderColor: BRAND.taupeLight,
                          borderRadius: '2px',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = BRAND.maroon }}
                        onBlur={(e) => { e.target.style.borderColor = BRAND.taupeLight }}
                        placeholder="Tell us about your travel preferences, special requirements, or any questions..."
                      />
                    </div>

                    {submitError && (
                      <div
                        className="p-4"
                        style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '2px' }}
                      >
                        <p className="text-red-600 text-sm font-medium">{submitError}</p>
                      </div>
                    )}

                    {recaptchaEnabled && (
                      <div className="flex justify-center">
                        <div ref={recaptchaWidgetRef}></div>
                      </div>
                    )}

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 px-6 font-medium text-white inline-flex items-center justify-center gap-3 text-sm tracking-wider uppercase transition-all duration-300 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          backgroundColor: BRAND.maroon,
                          borderRadius: '2px',
                        }}
                      >
                        <Send className="h-4 w-4" />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ==================== FAQ SECTION ==================== */}
      <div className="pb-24 sm:pb-32" style={{ backgroundColor: BRAND.cream }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 pt-20 sm:pt-24">
          <div className="text-center mb-14">
            <p
              className="text-sm tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
            >
              Common Questions
            </p>
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
            >
              Frequently <span className="italic font-medium" style={{ color: BRAND.maroon }}>Asked</span>
            </h2>
            <div className="mt-4 mx-auto w-16 h-0.5" style={{ backgroundColor: BRAND.gold }} />
          </div>

          {(() => {
            const faqs = [
              {
                q: 'How far in advance should I book?',
                a: 'We recommend booking at least 2–3 months in advance for the best availability and prices, especially for peak season. If your destination requires a visa, you may need to book even earlier.',
              },
              {
                q: 'Do you offer travel insurance?',
                a: 'Yes, we can arrange comprehensive travel insurance to protect your trip investment and provide peace of mind. Please review the policy details carefully.',
              },
              {
                q: 'Can you create custom packages?',
                a: 'Absolutely! Our travel experts specialise in creating personalised itineraries tailored to your preferences and budget.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept bank transfers and credit cards.',
              },
              {
                q: 'Do you help with visas?',
                a: 'Unfortunately we cannot assist with obtaining visas directly, but we can recommend a suitable visa service company to help you.',
              },
              {
                q: 'Can you assist with Foreign Currency',
                a: 'Yes we can arrange that your foreign currency will be delivered to your office.',
              },
              {
                q: 'Do you have an esim for data.',
                a: 'Yes please scan the QR code on our website and also download the directives on how to activate it.',
              },
            ]

            const hasOddLast = faqs.length % 2 === 1

            return (
              <div className="mx-auto max-w-4xl grid grid-cols-1 gap-8 md:grid-cols-2">
                {faqs.map((faq, idx) => {
                  const isLastOdd = hasOddLast && idx === faqs.length - 1
                  return (
                    <div
                      key={faq.q}
                      className={`p-6 ${isLastOdd ? 'md:col-span-2 md:mx-auto' : ''}`}
                      style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '2px',
                        borderLeft: `2px solid ${BRAND.gold}`,
                      }}
                    >
                      <h4
                        className="text-base font-medium mb-2"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                      >
                        {faq.q}
                      </h4>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
                      >
                        {faq.a}
                      </p>
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>
      </div>
    </div>
  )
}